import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "tableBody",
    "editableRow",
    "rowNameInput",
    "rowUnitDisplay",
    "rowUnitSelect",
    "rowUnitConsistent",
    "rowUnitError",
    "rowPriceDisplay",
    "rowPriceInput",
    "rowPriceConsistent",
    "rowPriceError",
    "rowQuantityInput",
    "rowQuantityError",
    "rowCostDisplay",
    "rowSuggestions",
    "statusBadge",
    "errorContainer",
    "totalCost",
    "ingredientIdInput",
    "quantityHiddenInput",
    "newIngredientNameInput",
    "newIngredientUnitInput",
    "newIngredientPriceInput",
    "establishmentIdInput",
    "rowDeleteButton",
  ];

  static values = {
    establishmentId: Number,
    searchUrl: String,
  };

  static outlets = ["cost-calculator"];

  connect() {
    this.searchTimeouts = new Map();
    this.rowStates = new Map(); // Track state of each row
    this.setupInitialRows();
    this.setupFormSubmissionPrevention();
    // Calculate initial total after setup
    setTimeout(() => this.updateTotalCost(), 100);
  }

  setupFormSubmissionPrevention() {
    // Add form-level event listener to prevent Enter key from submitting the form
    // when focus is within the ingredients table
    const form = this.element.closest("form");
    if (form) {
      form.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") return;
        if (!this.element.contains(event.target)) return;

        const target = event.target;
        const isTextarea = target.tagName === "TEXTAREA";
        const hasClosest = (selector) =>
          typeof target.closest === "function"
            ? target.closest(selector)
            : null;
        const isContentEditable =
          target.isContentEditable || !!hasClosest("[contenteditable='true']");
        const allowEnter =
          target.dataset.allowEnter === "true" ||
          !!hasClosest("[data-allow-enter='true']");

        if (isTextarea || isContentEditable || allowEnter) {
          return;
        }

        // Prevent Enter from submitting when inside the spreadsheet controls
        event.preventDefault();
        event.stopPropagation();
      });

      // Add form submission validation
      form.addEventListener("submit", (event) => {
        if (!this.validateAndShowErrors()) {
          // If validation fails, prevent submission
          event.preventDefault();
          event.stopPropagation();

          // Scroll to first error
          const firstError = this.element.querySelector(
            '[data-ingredients-table-spreadsheet-target="rowQuantityError"]:not(.hidden)'
          );
          if (firstError) {
            firstError.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      });
    }
  }

  setupInitialRows() {
    this.editableRowTargets.forEach((row, index) => {
      const rowIndex = row.dataset.rowIndex;
      const rowState = row.dataset.state || "empty";

      // If this is an existing ingredient row, set up proper state
      if (rowState === "existing_ingredient") {
        this.rowStates.set(rowIndex, {
          state: "existing_ingredient",
          ingredient: null, // We'll get price from DOM when needed
          isNew: false,
        });
      } else {
        this.rowStates.set(rowIndex, {
          state: "empty",
          ingredient: null,
          isNew: false,
        });
      }
    });
  }

  // Search functionality for specific row
  searchInRow(event) {
    const input = event.target;
    const rowIndex = input.dataset.rowIndex;
    const query = input.value.trim();

    // Auto-expand: if user is typing in one of the last empty rows, add more rows
    if (query.length >= 1 && this.isLastEmptyRow(rowIndex)) {
      this.triggerAutoExpansion();
    }

    // Clear previous timeout for this row
    if (this.searchTimeouts.has(rowIndex)) {
      clearTimeout(this.searchTimeouts.get(rowIndex));
    }

    if (query.length < 2) {
      this.hideSuggestionsForRow(rowIndex);
      // Only reset if this was previously a new ingredient or if it's completely empty
      // AND the input is completely empty (not just less than 2 chars)
      const currentState = this.rowStates.get(rowIndex);
      if (
        query.length === 0 && // Only reset when input is completely empty
        currentState &&
        (currentState.state === "new_ingredient" ||
          currentState.state === "new_ingredient_complete")
      ) {
        this.resetRowPartially(rowIndex);
      }
      return;
    }

    // Set new timeout for this row
    const timeout = setTimeout(() => {
      this.performSearchForRow(rowIndex, query);
    }, 250);

    this.searchTimeouts.set(rowIndex, timeout);
  }

  async performSearchForRow(rowIndex, query) {
    try {
      const response = await fetch(`${this.searchUrlValue}?q=${query}`, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Handle the response format from the controller
      let ingredients = [];
      if (data.status === "success" && Array.isArray(data.ingredients)) {
        ingredients = data.ingredients;
      } else if (Array.isArray(data)) {
        // Fallback for direct array response
        ingredients = data;
      } else {
        console.warn("Unexpected response format:", data);
        ingredients = [];
      }

      this.showSuggestionsForRow(rowIndex, ingredients, query);
    } catch (error) {
      console.error("Search error:", error);
      // Show empty results on error
      this.showSuggestionsForRow(rowIndex, [], query);
    }
  }

  showSuggestionsForRow(rowIndex, ingredients, query) {
    const suggestionsContainer = this.getSuggestionsContainerForRow(rowIndex);
    if (!suggestionsContainer) return;

    suggestionsContainer.innerHTML = "";
    suggestionsContainer.classList.remove("hidden");
    // Pin the dropdown with fixed positioning so it escapes the table's
    // overflow/stacking context and paints above subsequent rows
    this.positionSuggestionsForRow(rowIndex);

    // Group by master ingredient; masterless items kept separate
    const groups = new Map();
    const masterless = [];
    ingredients.forEach((ingredient) => {
      if (ingredient.master_ingredient_id) {
        const key = ingredient.master_ingredient_id;
        if (!groups.has(key)) groups.set(key, { name: ingredient.master_ingredient_name, items: [] });
        groups.get(key).items.push(ingredient);
      } else {
        masterless.push(ingredient);
      }
    });

    groups.forEach((group) => {
      const headerEl = document.createElement("div");
      headerEl.className = "px-3 py-1 text-xs font-semibold uppercase tracking-wide text-base-content/40 bg-base-100 border-b border-base-300/30 select-none";
      headerEl.textContent = group.name;
      suggestionsContainer.appendChild(headerEl);
      group.items.forEach((ingredient) => {
        suggestionsContainer.appendChild(this.createSuggestionElement(ingredient, rowIndex));
      });
    });

    if (masterless.length > 0) {
      if (groups.size > 0) {
        const divider = document.createElement("div");
        divider.className = "border-t border-base-300/50 my-0.5";
        suggestionsContainer.appendChild(divider);
      }
      masterless.forEach((ingredient) => {
        suggestionsContainer.appendChild(this.createSuggestionElement(ingredient, rowIndex));
      });
    }

    // Add "create new" option
    this.showNewIngredientOptionForRow(rowIndex, query, suggestionsContainer);
  }

  createSuggestionElement(ingredient, rowIndex) {
    const ingredientName = ingredient.ingredient_name || ingredient.name;
    const labelParts = [ingredientName, ingredient.supplier_name].filter(Boolean);
    const label = labelParts.join(" · ");

    const suggestionEl = document.createElement("div");
    suggestionEl.className =
      "px-3 py-2 hover:bg-base-200 cursor-pointer border-b border-base-300/50 transition-colors";
    suggestionEl.innerHTML = `
      <div class="flex justify-between items-center gap-3">
        <div class="font-medium text-base-content truncate">${this.escapeHtml(label)}</div>
        <div class="text-xs text-base-content/50 shrink-0">(${ingredient.unit})</div>
      </div>
    `;

    suggestionEl.dataset.ingredient = JSON.stringify(ingredient);
    suggestionEl.dataset.rowIndex = rowIndex;
    suggestionEl.dataset.action =
      "click->ingredients-table-spreadsheet#selectSuggestionForRow";

    return suggestionEl;
  }

  showNewIngredientOptionForRow(rowIndex, query, container) {
    const trimmed = (query || "").trim();
    if (!trimmed) return;

    const newOptionEl = document.createElement("div");
    newOptionEl.className =
      "px-3 py-2 hover:bg-primary/10 cursor-pointer border-t border-primary/20 bg-primary/5";
    newOptionEl.innerHTML = `
      <div class="flex items-center text-primary text-sm">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        <span class="font-medium">+ Créer « ${this.escapeHtml(trimmed)} » comme nouvel ingrédient</span>
      </div>
    `;

    newOptionEl.dataset.action =
      "click->ingredients-table-spreadsheet#createNewIngredientForRow";
    newOptionEl.dataset.name = query;
    newOptionEl.dataset.rowIndex = rowIndex;

    container.appendChild(newOptionEl);
  }

  selectSuggestionForRow(event) {
    const ingredient = JSON.parse(event.currentTarget.dataset.ingredient);
    const rowIndex = event.currentTarget.dataset.rowIndex;

    this.setRowWithExistingIngredient(rowIndex, ingredient);
    this.hideSuggestionsForRow(rowIndex);
    this.focusQuantityForRow(rowIndex);
  }

  createNewIngredientForRow(event) {
    const name = event.currentTarget.dataset.name;
    const rowIndex = event.currentTarget.dataset.rowIndex;

    this.setRowForNewIngredient(rowIndex, name);
    this.hideSuggestionsForRow(rowIndex);
    this.focusUnitForRow(rowIndex);
  }

  setRowWithExistingIngredient(rowIndex, ingredient) {
    const row = this.getRowByIndex(rowIndex);
    if (!row) return;

    // Update row state
    this.rowStates.set(rowIndex, {
      state: "existing_ingredient",
      ingredient: ingredient,
      isNew: false,
    });

    // Update UI elements
    const ingredientName = ingredient.ingredient_name || ingredient.name;
    const masterName = ingredient.master_ingredient_name;
    const nameParts = [];
    if (masterName && masterName !== ingredientName) nameParts.push(masterName);
    nameParts.push(ingredientName);
    if (ingredient.supplier_name) nameParts.push(ingredient.supplier_name);
    this.getNameInputForRow(rowIndex).value = nameParts.join(" · ");

    // Use consistent display for existing ingredients
    this.getUnitConsistentForRow(rowIndex).textContent = ingredient.unit;
    this.getPriceConsistentForRow(rowIndex).textContent = this.formatCurrency(
      ingredient.price
    );

    // Hide old display elements and edit elements
    this.getUnitDisplayForRow(rowIndex).classList.add("hidden");
    this.getPriceDisplayForRow(rowIndex).classList.add("hidden");
    this.getUnitSelectForRow(rowIndex).classList.add("hidden");
    this.getPriceInputForRow(rowIndex).classList.add("hidden");

    // Show consistent elements
    this.getUnitConsistentForRow(rowIndex).classList.remove("hidden");
    this.getPriceConsistentForRow(rowIndex).classList.remove("hidden");

    // Enable quantity input
    this.getQuantityInputForRow(rowIndex).disabled = false;

    // Update hidden form fields
    this.enableHiddenFields(rowIndex);
    this.updateHiddenFieldsForExistingIngredient(rowIndex, ingredient);

    // Update row visual state
    row.dataset.state = "existing_ingredient";
    this.showStatusBadge(rowIndex, "Existant", "badge-accent");
    this.showDeleteButton(rowIndex);
  }

  setRowForNewIngredient(rowIndex, name) {
    const row = this.getRowByIndex(rowIndex);
    if (!row) return;

    // Update row state
    this.rowStates.set(rowIndex, {
      state: "new_ingredient",
      ingredient: { name: name, isNew: true },
      isNew: true,
    });

    // Update UI elements
    this.getNameInputForRow(rowIndex).value = name;

    // Use consistent display for visual uniformity, but enable interactivity
    this.getUnitConsistentForRow(rowIndex).textContent = "Sélectionnez...";
    this.getUnitConsistentForRow(rowIndex).classList.add(
      "cursor-pointer",
      "hover:bg-base-100"
    );
    this.getPriceConsistentForRow(rowIndex).textContent = "Prix...";
    this.getPriceConsistentForRow(rowIndex).classList.add(
      "cursor-pointer",
      "hover:bg-base-100"
    );

    // Hide old display elements and actual form elements (we'll use hidden ones)
    this.getUnitDisplayForRow(rowIndex).classList.add("hidden");
    this.getPriceDisplayForRow(rowIndex).classList.add("hidden");
    this.getUnitSelectForRow(rowIndex).classList.add("hidden");
    this.getPriceInputForRow(rowIndex).classList.add("hidden");

    // Show consistent elements
    this.getUnitConsistentForRow(rowIndex).classList.remove("hidden");
    this.getPriceConsistentForRow(rowIndex).classList.remove("hidden");

    // Add click handlers for the consistent elements to show editing interface
    this.setupNewIngredientInteractions(rowIndex);

    // Disable quantity until unit and price are set
    this.getQuantityInputForRow(rowIndex).disabled = true;

    // Update row visual state
    row.dataset.state = "new_ingredient";
    this.showStatusBadge(rowIndex, "Nouveau", "badge-primary");
    this.showDeleteButton(rowIndex);
  }

  setupNewIngredientInteractions(rowIndex) {
    const unitElement = this.getUnitConsistentForRow(rowIndex);
    const priceElement = this.getPriceConsistentForRow(rowIndex);

    // Remove existing event listeners to prevent duplicates
    unitElement.replaceWith(unitElement.cloneNode(true));
    priceElement.replaceWith(priceElement.cloneNode(true));

    // Get fresh references after cloning
    const freshUnitElement = this.getUnitConsistentForRow(rowIndex);
    const freshPriceElement = this.getPriceConsistentForRow(rowIndex);

    // Add click handlers
    freshUnitElement.addEventListener("click", (e) =>
      this.showUnitSelector(rowIndex)
    );
    freshPriceElement.addEventListener("click", (e) =>
      this.showPriceEditor(rowIndex)
    );
  }

  showUnitSelector(rowIndex) {
    const unitElement = this.getUnitConsistentForRow(rowIndex);
    const currentValue = this.getUnitSelectForRow(rowIndex).value;

    // Create a temporary select dropdown
    const tempSelect = document.createElement("select");
    tempSelect.className =
      "w-full px-2 py-1 text-sm border border-primary rounded focus:ring-1 focus:ring-primary";
    tempSelect.innerHTML = `
      <option value="">Sélectionnez une unité</option>
      <option value="kg">kg</option>
      <option value="l">l</option>
      <option value="pièce">pièce</option>
      <option value="g">g</option>
      <option value="ml">ml</option>
    `;
    tempSelect.value = currentValue;

    // Replace the display element temporarily
    unitElement.style.display = "none";
    unitElement.parentNode.insertBefore(tempSelect, unitElement);
    tempSelect.focus();

    // Handle selection
    const handleSelection = (e) => {
      const value = tempSelect.value;
      if (value) {
        unitElement.textContent = value;
        this.getUnitSelectForRow(rowIndex).value = value;
        this.updateNewIngredientData(rowIndex, "unit", value);
        this.checkNewIngredientCompletion(rowIndex);
        // Hide unit error when a valid unit is selected
        this.hideUnitError(rowIndex);
      } else {
        unitElement.textContent = "Sélectionnez...";
      }

      // Restore original element
      tempSelect.remove();
      unitElement.style.display = "";
    };

    tempSelect.addEventListener("change", handleSelection);
    tempSelect.addEventListener("blur", handleSelection);
    tempSelect.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        tempSelect.remove();
        unitElement.style.display = "";
      }
    });
  }

  showPriceEditor(rowIndex) {
    const priceElement = this.getPriceConsistentForRow(rowIndex);
    const currentValue = this.getPriceInputForRow(rowIndex).value;

    // Create a temporary input
    const tempInput = document.createElement("input");
    tempInput.type = "number";
    tempInput.step = "0.01";
    tempInput.min = "0";
    tempInput.className =
      "w-full px-2 py-1 text-sm border border-primary rounded focus:ring-1 focus:ring-primary";
    tempInput.value = currentValue;
    tempInput.placeholder = "Prix par unité";

    // Replace the display element temporarily
    priceElement.style.display = "none";
    priceElement.parentNode.insertBefore(tempInput, priceElement);
    tempInput.focus();
    tempInput.select();

    // Handle input
    const handleInput = (e) => {
      const value = parseFloat(tempInput.value) || 0;
      if (value > 0) {
        priceElement.textContent = this.formatCurrency(value);
        this.getPriceInputForRow(rowIndex).value = value;
        this.updateNewIngredientData(rowIndex, "price", value);
        this.checkNewIngredientCompletion(rowIndex);
        // Hide price error when a valid price is entered
        this.hidePriceError(rowIndex);
      } else {
        priceElement.textContent = "Prix...";
      }

      // Restore original element
      tempInput.remove();
      priceElement.style.display = "";
    };

    tempInput.addEventListener("blur", handleInput);
    tempInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleInput();
        // Focus quantity input if everything is ready
        const state = this.rowStates.get(rowIndex);
        if (
          state &&
          state.ingredient &&
          state.ingredient.unit &&
          state.ingredient.price > 0
        ) {
          this.focusQuantityForRow(rowIndex);
        }
      } else if (e.key === "Escape") {
        tempInput.remove();
        priceElement.style.display = "";
      }
    });
  }

  onUnitChange(event) {
    const rowIndex = event.target.dataset.rowIndex;
    const unit = event.target.value;

    // Hide unit error when a valid unit is selected
    if (unit && unit !== "") {
      this.hideUnitError(rowIndex);
    }

    this.updateNewIngredientData(rowIndex, "unit", unit);
    this.checkNewIngredientCompletion(rowIndex);
  }

  onPriceChange(event) {
    const rowIndex = event.target.dataset.rowIndex;
    const price = parseFloat(event.target.value) || 0;

    // Hide price error when a valid price is entered
    if (price > 0) {
      this.hidePriceError(rowIndex);
    }

    this.updateNewIngredientData(rowIndex, "price", price);
    this.checkNewIngredientCompletion(rowIndex);
  }

  updateNewIngredientData(rowIndex, field, value) {
    const state = this.rowStates.get(rowIndex);
    if (state && state.ingredient) {
      state.ingredient[field] = value;
      this.rowStates.set(rowIndex, state);
    }
  }

  checkNewIngredientCompletion(rowIndex) {
    const state = this.rowStates.get(rowIndex);
    if (!state || !state.isNew) return;

    const unit = state.ingredient.unit || "";
    const price = parseFloat(state.ingredient.price) || 0;

    if (unit && price > 0) {
      // Enable quantity input
      this.getQuantityInputForRow(rowIndex).disabled = false;
      this.enableHiddenFields(rowIndex);
      this.updateHiddenFieldsForNewIngredient(rowIndex, state.ingredient);
    } else {
      // Disable quantity input
      this.getQuantityInputForRow(rowIndex).disabled = true;
    }
  }

  calculateRowCost(event) {
    const rowIndex = event.target.dataset.rowIndex;
    const quantity = parseFloat(event.target.value) || 0;

    // Hide error when user enters a valid quantity
    if (quantity > 0) {
      this.hideQuantityError(rowIndex);
    }

    const row = this.getRowByIndex(rowIndex);
    const rowState = row.dataset.state;

    let price = 0;

    // For existing ingredients, get price from the display element
    if (rowState === "existing_ingredient") {
      const priceElement = this.getPriceConsistentForRow(rowIndex);
      if (priceElement) {
        const priceText = priceElement.textContent
          .replace(/[^\d,.-]/g, "") // Keep digits, comma, dot, and minus
          .replace(",", "."); // Convert French decimal comma to dot
        price = parseFloat(priceText) || 0;
      }
    } else {
      // For new ingredients, get price from state
      const state = this.rowStates.get(rowIndex);
      if (!state || !state.ingredient) {
        return;
      }
      price = parseFloat(state.ingredient.price) || 0;
    }

    const cost = quantity * price;
    const costDisplay = this.getCostDisplayForRow(rowIndex);
    const formattedCost = this.formatCurrency(cost);
    costDisplay.textContent = formattedCost;

    // Update hidden quantity field
    this.updateHiddenQuantity(rowIndex, quantity);

    // Trigger total recalculation
    this.updateTotalCost();

    // For new ingredients only: If this was the last empty row and now has content, add a new empty row
    if (rowState !== "existing_ingredient") {
      const state = this.rowStates.get(rowIndex);
      if (
        this.isLastEmptyRow(rowIndex) &&
        quantity > 0 &&
        state &&
        state.ingredient
      ) {
        this.addNewEmptyRow();
      }
    }

    // Trigger cost calculator recalculation
    if (this.hasCostCalculatorOutlet) {
      this.costCalculatorOutlet.recalculate();
    }
  }

  // Navigation and keyboard handling
  handleRowKeydown(event) {
    const rowIndex = event.target.dataset.rowIndex;

    switch (event.key) {
      case "Tab":
        this.handleTabNavigation(event, rowIndex);
        break;
      case "Enter":
        event.preventDefault();
        event.stopPropagation();
        this.handleEnterKey(event, rowIndex);
        break;
      case "Escape":
        this.hideSuggestionsForRow(rowIndex);
        break;
      case "ArrowDown":
      case "ArrowUp":
        if (
          !this.getSuggestionsContainerForRow(rowIndex).classList.contains(
            "hidden"
          )
        ) {
          event.preventDefault();
          this.navigateSuggestions(
            rowIndex,
            event.key === "ArrowDown" ? 1 : -1
          );
        }
        break;
    }
  }

  handleQuantityKeydown(event) {
    const rowIndex = event.target.dataset.rowIndex;

    // Prevent form submission on Enter key
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      this.handleEnterKey(event, rowIndex);
      return;
    }

    if (event.key === "Tab") {
      this.handleTabNavigation(event, rowIndex);
    }
  }

  handleTabNavigation(event, rowIndex) {
    // Let default tab behavior handle navigation between inputs in the same row
    // Custom handling only for special cases
  }

  handleEnterKey(event, rowIndex) {
    const target = event.target;

    if (target.dataset.ingredientsTableSpreadsheetTarget === "rowNameInput") {
      // If suggestions are visible, select first one
      const suggestionsContainer = this.getSuggestionsContainerForRow(rowIndex);
      if (!suggestionsContainer.classList.contains("hidden")) {
        const firstSuggestion = suggestionsContainer.querySelector(
          '[data-action*="selectSuggestionForRow"]'
        );
        if (firstSuggestion) {
          firstSuggestion.click();
          return;
        }
      }
      // If no suggestions visible and input has value, move to quantity
      if (target.value.trim()) {
        this.focusQuantityForRow(rowIndex);
      }
    } else if (
      target.dataset.ingredientsTableSpreadsheetTarget === "rowQuantityInput"
    ) {
      // Move to next row name input
      this.moveToNextRowName(rowIndex);
    }
  }

  moveToNextRowName(currentRowIndex) {
    const nextRowIndex = parseInt(currentRowIndex) + 1;
    const nextRow = this.getRowByIndex(nextRowIndex);

    if (nextRow) {
      this.getNameInputForRow(nextRowIndex).focus();
    } else {
      // Add new row if we're at the end
      this.addNewEmptyRow();
      const newRowIndex = this.editableRowTargets.length - 1;
      setTimeout(() => {
        this.getNameInputForRow(newRowIndex).focus();
      }, 100);
    }
  }

  navigateSuggestions(rowIndex, direction) {
    const suggestionsContainer = this.getSuggestionsContainerForRow(rowIndex);
    if (!suggestionsContainer) return;

    const suggestions = suggestionsContainer.querySelectorAll(
      '[data-action*="selectSuggestionForRow"], [data-action*="createNewIngredientForRow"]'
    );
    if (suggestions.length === 0) return;

    let currentIndex = -1;
    const activeSuggestion =
      suggestionsContainer.querySelector(".bg-primary\\/10");

    if (activeSuggestion) {
      currentIndex = Array.from(suggestions).indexOf(activeSuggestion);
      activeSuggestion.classList.remove("bg-primary/10");
    }

    currentIndex += direction;

    if (currentIndex < 0) currentIndex = suggestions.length - 1;
    if (currentIndex >= suggestions.length) currentIndex = 0;

    suggestions[currentIndex].classList.add("bg-primary/10");
    suggestions[currentIndex].scrollIntoView({ block: "nearest" });
  }

  focusQuantityForRow(rowIndex) {
    const quantityInput = this.getQuantityInputForRow(rowIndex);
    if (quantityInput) {
      quantityInput.focus();
    }
  }

  focusUnitForRow(rowIndex) {
    const unitSelect = this.getUnitSelectForRow(rowIndex);
    if (unitSelect) {
      unitSelect.focus();
    }
  }

  // Row management
  clearRow(event) {
    event.preventDefault();
    event.stopPropagation();

    // Get rowIndex from the button element, not the clicked element (could be SVG)
    const button = event.currentTarget;
    const rowIndex = button.dataset.rowIndex;
    console.log("Clearing row:", rowIndex);

    // Clear all errors when clearing the row
    this.hideQuantityError(rowIndex);
    this.hideUnitError(rowIndex);
    this.hidePriceError(rowIndex);
    this.resetRowToEmpty(rowIndex);
  }

  removeExistingIngredient(event) {
    event.preventDefault();
    event.stopPropagation();

    // Get rowIndex from the button element, not the clicked element (could be SVG)
    const button = event.currentTarget;
    const rowIndex = button.dataset.rowIndex;
    console.log("Removing existing ingredient from row:", rowIndex);

    const row = this.getRowByIndex(rowIndex);

    if (row) {
      // Debug: log all inputs in the row
      const allInputs = row.querySelectorAll("input");
      console.log("All inputs in row:", allInputs);

      // For existing ingredients, look for the Rails-generated _destroy field
      // It could be either with our target or a standard Rails field
      let destroyInput = row.querySelector(
        '[data-ingredients-table-spreadsheet-target="destroyInput"]'
      );

      // If not found with our target, look for Rails standard _destroy field
      if (!destroyInput) {
        destroyInput = row.querySelector('input[name*="_destroy"]');
      }

      // If still not found, try more specific patterns
      if (!destroyInput) {
        destroyInput = row.querySelector(
          'input[name*="technical_sheet_ingredients_attributes"][name*="_destroy"]'
        );
      }

      if (destroyInput) {
        destroyInput.value = "1";
        console.log(
          "Set destroy field to 1 for input:",
          destroyInput.name,
          "Current value:",
          destroyInput.value
        );

        // Also ensure the input is enabled (not disabled)
        destroyInput.disabled = false;

        // Mark the row as destroyed for our internal tracking
        row.dataset.destroyed = "true";
      } else {
        console.error("Could not find _destroy input field in row");
        console.log(
          "Available input names:",
          Array.from(allInputs).map((input) => input.name)
        );
      }

      // Hide the row visually
      row.style.display = "none";
      console.log("Hidden row visually");

      // Update total cost calculation
      this.updateTotalCost();

      // Trigger cost calculator recalculation with a small delay to ensure DOM is updated
      setTimeout(() => {
        if (this.hasCostCalculatorOutlet) {
          console.log("Triggering cost calculator after ingredient removal");
          this.costCalculatorOutlet.recalculate();
        }
      }, 50);
    }
  }

  resetRowToEmpty(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    if (!row) return;

    // Reset row state
    this.rowStates.set(rowIndex, {
      state: "empty",
      ingredient: null,
      isNew: false,
    });

    // Clear all inputs
    this.getNameInputForRow(rowIndex).value = "";
    this.getQuantityInputForRow(rowIndex).value = "";
    this.getQuantityInputForRow(rowIndex).disabled = true;

    if (this.getUnitSelectForRow(rowIndex)) {
      this.getUnitSelectForRow(rowIndex).value = "";
    }
    if (this.getPriceInputForRow(rowIndex)) {
      this.getPriceInputForRow(rowIndex).value = "";
    }

    // Reset display elements
    this.getUnitConsistentForRow(rowIndex).textContent = "-";
    this.getPriceConsistentForRow(rowIndex).textContent = "-";
    this.getCostDisplayForRow(rowIndex).textContent = "-";

    // Show consistent elements, hide all others
    this.getUnitDisplayForRow(rowIndex).classList.add("hidden");
    this.getPriceDisplayForRow(rowIndex).classList.add("hidden");
    this.getUnitSelectForRow(rowIndex).classList.add("hidden");
    this.getPriceInputForRow(rowIndex).classList.add("hidden");
    this.getUnitConsistentForRow(rowIndex).classList.remove("hidden");
    this.getPriceConsistentForRow(rowIndex).classList.remove("hidden");

    // Clear hidden form fields
    this.clearHiddenFields(rowIndex);

    // Update row visual state
    row.dataset.state = "empty";
    this.hideStatusBadge(rowIndex);
    this.hideDeleteButton(rowIndex);

    // Update totals
    this.updateTotalCost();
  }

  resetRowPartially(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    if (!row) return;

    // Reset row state but keep the current name input value
    this.rowStates.set(rowIndex, {
      state: "empty",
      ingredient: null,
      isNew: false,
    });

    // Clear quantity and disable it
    this.getQuantityInputForRow(rowIndex).value = "";
    this.getQuantityInputForRow(rowIndex).disabled = true;

    // Reset unit and price inputs
    this.getUnitSelectForRow(rowIndex).value = "";
    this.getPriceInputForRow(rowIndex).value = "";

    // Reset consistent display elements
    const unitConsistent = this.getUnitConsistentForRow(rowIndex);
    const priceConsistent = this.getPriceConsistentForRow(rowIndex);

    unitConsistent.textContent = "-";
    priceConsistent.textContent = "-";
    unitConsistent.classList.remove("cursor-pointer", "hover:bg-base-100");
    priceConsistent.classList.remove("cursor-pointer", "hover:bg-base-100");

    // Reset cost display
    this.getCostDisplayForRow(rowIndex).textContent = "-";

    // Hide old display elements and form elements, show consistent ones
    this.getUnitDisplayForRow(rowIndex).classList.add("hidden");
    this.getPriceDisplayForRow(rowIndex).classList.add("hidden");
    this.getUnitSelectForRow(rowIndex).classList.add("hidden");
    this.getPriceInputForRow(rowIndex).classList.add("hidden");

    unitConsistent.classList.remove("hidden");
    priceConsistent.classList.remove("hidden");

    // Clear hidden form fields but don't clear the name input
    this.clearHiddenFields(rowIndex);

    // Update row visual state
    row.dataset.state = "empty";
    this.hideStatusBadge(rowIndex);
    this.hideDeleteButton(rowIndex);

    // Only update totals if this was actually changing a cost-affecting row
    // Don't recalculate totals unnecessarily during typing
  }

  addNewEmptyRow() {
    // Trigger the nested-form controller to add more ingredient rows
    this.triggerAutoExpansion();
  }

  triggerAutoExpansion() {
    // Find the nested-form controller in parent elements
    const nestedFormController =
      this.application.getControllerForElementAndIdentifier(
        this.element,
        "nested-form"
      );

    if (nestedFormController) {
      // Create a fake event to trigger the add method
      const fakeEvent = {
        preventDefault: () => {},
        params: { type: "ingredient-row" },
      };
      nestedFormController.add(fakeEvent);
    } else {
      // Fallback: try to find and click the add button
      const addButton = this.element.querySelector(
        '[data-nested-form-type-param="ingredient-row"]'
      );
      if (addButton) {
        addButton.click();
      }
    }
  }

  isLastEmptyRow(rowIndex) {
    const allRows = this.editableRowTargets;
    const currentRowPosition = allRows.findIndex(
      (row) => row.dataset.rowIndex === rowIndex
    );

    // Check if this is one of the last 2 rows
    return currentRowPosition >= allRows.length - 2;
  }

  // Utility methods for DOM access
  getRowByIndex(rowIndex) {
    return this.editableRowTargets.find(
      (row) => row.dataset.rowIndex === rowIndex
    );
  }

  getNameInputForRow(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    return row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="rowNameInput"]'
    );
  }

  getUnitDisplayForRow(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    return row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="rowUnitDisplay"]'
    );
  }

  getUnitSelectForRow(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    return row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="rowUnitSelect"]'
    );
  }

  getPriceDisplayForRow(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    return row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="rowPriceDisplay"]'
    );
  }

  getPriceInputForRow(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    return row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="rowPriceInput"]'
    );
  }

  getUnitConsistentForRow(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    return row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="rowUnitConsistent"]'
    );
  }

  getPriceConsistentForRow(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    return row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="rowPriceConsistent"]'
    );
  }

  getQuantityInputForRow(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    return row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="rowQuantityInput"]'
    );
  }

  getCostDisplayForRow(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    return row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="rowCostDisplay"]'
    );
  }

  getSuggestionsContainerForRow(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    return row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="rowSuggestions"]'
    );
  }

  getDeleteButtonForRow(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    return row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="rowDeleteButton"]'
    );
  }

  hideSuggestionsForRow(rowIndex) {
    const container = this.getSuggestionsContainerForRow(rowIndex);
    if (container) {
      container.classList.add("hidden");
      container.style.position = "";
      container.style.top = "";
      container.style.left = "";
      container.style.width = "";
      container.style.right = "";
      container.style.zIndex = "";
    }
    if (this.openDropdownRow == rowIndex) {
      this.untrackOpenDropdown();
    }
  }

  // Pin the row's dropdown to the input using fixed positioning so it is not
  // clipped by the table's overflow wrapper nor hidden behind later rows.
  // Fixed positioning is the only way to escape an overflow ancestor, so we
  // keep it in sync with scroll/resize (the Floating UI approach) to avoid the
  // dropdown drifting away from its input while the page scrolls.
  positionSuggestionsForRow(rowIndex) {
    const container = this.getSuggestionsContainerForRow(rowIndex);
    const input = this.getNameInputForRow(rowIndex);
    if (!container || !input) return;

    const rect = input.getBoundingClientRect();

    // Close the dropdown once the input scrolls out of the viewport rather than
    // letting it float over unrelated content (e.g. the sticky header).
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      this.hideSuggestionsForRow(rowIndex);
      return;
    }

    container.style.position = "fixed";
    container.style.top = `${rect.bottom + 2}px`;
    container.style.left = `${rect.left}px`;
    container.style.width = `${rect.width}px`;
    container.style.right = "auto";
    container.style.zIndex = "1000";

    this.trackOpenDropdown(rowIndex);
  }

  // Register scroll/resize listeners (once) so the open dropdown follows its
  // input. Capture phase catches scrolling on any ancestor, not just window.
  trackOpenDropdown(rowIndex) {
    this.openDropdownRow = rowIndex;
    if (this.repositionHandler) return;

    this.repositionHandler = () => {
      if (this.openDropdownRow != null) {
        this.positionSuggestionsForRow(this.openDropdownRow);
      }
    };
    window.addEventListener("scroll", this.repositionHandler, true);
    window.addEventListener("resize", this.repositionHandler);
  }

  untrackOpenDropdown() {
    this.openDropdownRow = null;
    if (this.repositionHandler) {
      window.removeEventListener("scroll", this.repositionHandler, true);
      window.removeEventListener("resize", this.repositionHandler);
      this.repositionHandler = null;
    }
  }

  // Form field management
  updateHiddenFieldsForExistingIngredient(rowIndex, ingredient) {
    const row = this.getRowByIndex(rowIndex);
    const ingredientIdInput = row?.querySelector(
      'input[name$="[ingredient_id]"]'
    );
    const masterIngredientIdInput = row?.querySelector(
      'input[name$="[master_ingredient_id]"]'
    );
    const supplierIdInput = row?.querySelector(
      'input[name$="[supplier_id]"]'
    );

    if (ingredientIdInput) {
      ingredientIdInput.value = ingredient.ingredient_id || ingredient.id;
    }
    if (masterIngredientIdInput) {
      masterIngredientIdInput.value = ingredient.master_ingredient_id || "";
    }
    if (supplierIdInput) {
      supplierIdInput.value = ingredient.supplier_id || "";
    }
  }

  updateHiddenFieldsForNewIngredient(rowIndex, ingredient) {
    const row = this.getRowByIndex(rowIndex);

    const nameInput = row?.querySelector(
      'input[name*="[ingredient_attributes][name]"]'
    );
    const unitInput = row?.querySelector(
      'input[name*="[ingredient_attributes][unit]"]'
    );
    const priceInput = row?.querySelector(
      'input[name*="[ingredient_attributes][price]"]'
    );

    if (nameInput) nameInput.value = ingredient.name;
    if (unitInput) unitInput.value = ingredient.unit;
    if (priceInput) priceInput.value = ingredient.price;
  }

  updateHiddenQuantity(rowIndex, quantity) {
    const row = this.getRowByIndex(rowIndex);
    const quantityInput = row?.querySelector('input[name$="[quantity]"]');

    if (quantityInput) {
      quantityInput.value = quantity;
    }
  }

  clearHiddenFields(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    const hiddenInputs = row?.querySelectorAll('input[type="hidden"]');

    hiddenInputs?.forEach((input) => {
      if (!input.name.includes("establishment_id")) {
        input.value = "";
        input.disabled = true; // Disable empty fields so they won't be submitted
      }
    });
  }

  enableHiddenFields(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    const hiddenInputs = row?.querySelectorAll('input[type="hidden"]');

    hiddenInputs?.forEach((input) => {
      input.disabled = false; // Enable fields for submission
    });
  }

  // UI feedback methods
  showStatusBadge(rowIndex, text, className) {
    const row = this.getRowByIndex(rowIndex);
    const badge = row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="statusBadge"]'
    );

    if (badge) {
      badge.textContent = text;
      badge.className = `badge badge-xs ${className}`;
      badge.classList.remove("hidden");
    }
  }

  hideStatusBadge(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    const badge = row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="statusBadge"]'
    );

    if (badge) {
      badge.classList.add("hidden");
    }
  }

  showDeleteButton(rowIndex) {
    const deleteButton = this.getDeleteButtonForRow(rowIndex);
    if (deleteButton) {
      deleteButton.classList.remove("hidden");
    }
  }

  hideDeleteButton(rowIndex) {
    const deleteButton = this.getDeleteButtonForRow(rowIndex);
    if (deleteButton) {
      deleteButton.classList.add("hidden");
    }
  }

  // Cost calculation
  updateTotalCost() {
    let total = 0;

    // Get costs from ALL rows (both existing and new ingredients use the same target now)
    this.editableRowTargets.forEach((row, index) => {
      // Skip hidden rows (deleted ingredients)
      if (row.style.display === "none") {
        return;
      }

      // Skip rows marked for destruction
      if (row.dataset.destroyed === "true") {
        return;
      }

      const costDisplay = row.querySelector(
        '[data-ingredients-table-spreadsheet-target="rowCostDisplay"]'
      );
      if (costDisplay) {
        if (
          costDisplay.textContent !== "-" &&
          costDisplay.textContent.trim() !== ""
        ) {
          const costText = costDisplay.textContent
            .replace(/[^\d,.-]/g, "") // Keep digits, comma, dot, and minus
            .replace(",", "."); // Convert French decimal comma to dot
          const cost = parseFloat(costText) || 0;
          total += cost;
        }
      }
    });

    if (this.hasTotalCostTarget) {
      const formattedTotal = this.formatCurrency(total);
      this.totalCostTarget.textContent = formattedTotal;
    }
  }

  // Row focus/blur handlers
  onRowFocus(event) {
    const row = event.target.closest("tr");
    row?.classList.add("bg-base-50/80");
  }

  onRowBlur(event) {
    const row = event.target.closest("tr");
    // Add small delay to prevent flicker when moving between fields in same row
    setTimeout(() => {
      if (!row?.contains(document.activeElement)) {
        row?.classList.remove("bg-base-50/80");
      }
    }, 100);
  }

  // Utility methods
  formatCurrency(amount) {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  }

  // Form submission prevention is now handled in the individual keydown handlers

  // Validation methods for inline error display
  validateAndShowErrors() {
    this.clearAllQuantityErrors();
    this.clearAllUnitErrors();
    this.clearAllPriceErrors();
    let hasErrors = false;

    this.editableRowTargets.forEach((row) => {
      const rowIndex = row.dataset.rowIndex;
      const rowState = this.rowStates.get(rowIndex);

      // Skip empty rows
      if (!rowState || rowState.state === "empty") {
        return;
      }

      // Check if row has an ingredient name but is incomplete
      const nameInput = this.getNameInputForRow(rowIndex);
      if (nameInput.value.trim()) {
        // For existing ingredients, only check quantity
        if (rowState.state === "existing_ingredient") {
          const quantityInput = this.getQuantityInputForRow(rowIndex);
          const quantity = quantityInput.value.trim();

          if (!quantity || parseFloat(quantity) <= 0) {
            this.showQuantityError(rowIndex);
            hasErrors = true;
          }
        }

        // For new ingredients, check unit, price AND quantity
        else if (
          rowState.state === "new_ingredient" ||
          rowState.state === "new_ingredient_complete"
        ) {
          // Check unit selection
          const unitSelect = this.getUnitSelectForRow(rowIndex);
          const unitConsistent = this.getUnitConsistentForRow(rowIndex);

          // Check if unit is selected (either in dropdown or displayed in consistent view)
          const hasUnit =
            (unitSelect && unitSelect.value && unitSelect.value !== "") ||
            (unitConsistent &&
              unitConsistent.textContent.trim() !== "-" &&
              unitConsistent.textContent.trim() !== "");

          if (!hasUnit) {
            this.showUnitError(rowIndex);
            hasErrors = true;
          }

          // Check price input
          const priceInput = this.getPriceInputForRow(rowIndex);
          const priceConsistent = this.getPriceConsistentForRow(rowIndex);

          // Check if price is set (either in input field or displayed in consistent view)
          const hasPrice =
            (priceInput &&
              priceInput.value &&
              parseFloat(priceInput.value) > 0) ||
            (priceConsistent &&
              priceConsistent.textContent.trim() !== "-" &&
              priceConsistent.textContent.trim() !== "");

          if (!hasPrice) {
            this.showPriceError(rowIndex);
            hasErrors = true;
          }

          // Check quantity
          const quantityInput = this.getQuantityInputForRow(rowIndex);
          const quantity = quantityInput.value.trim();

          if (!quantity || parseFloat(quantity) <= 0) {
            this.showQuantityError(rowIndex);
            hasErrors = true;
          }
        }
      }
    });

    return !hasErrors; // Return true if no errors
  }

  showQuantityError(rowIndex) {
    const errorElement = this.getQuantityErrorForRow(rowIndex);
    if (errorElement) {
      errorElement.classList.remove("hidden");

      // Add error styling to the input field
      const quantityInput = this.getQuantityInputForRow(rowIndex);
      if (quantityInput) {
        quantityInput.classList.add("ring-2", "ring-error/50", "bg-error/5");
      }
    }
  }

  hideQuantityError(rowIndex) {
    const errorElement = this.getQuantityErrorForRow(rowIndex);
    if (errorElement) {
      errorElement.classList.add("hidden");

      // Remove error styling from the input field
      const quantityInput = this.getQuantityInputForRow(rowIndex);
      if (quantityInput) {
        quantityInput.classList.remove("ring-2", "ring-error/50", "bg-error/5");
      }
    }
  }

  clearAllQuantityErrors() {
    this.editableRowTargets.forEach((row) => {
      const rowIndex = row.dataset.rowIndex;
      this.hideQuantityError(rowIndex);
    });
  }

  clearAllUnitErrors() {
    this.editableRowTargets.forEach((row) => {
      const rowIndex = row.dataset.rowIndex;
      this.hideUnitError(rowIndex);
    });
  }

  clearAllPriceErrors() {
    this.editableRowTargets.forEach((row) => {
      const rowIndex = row.dataset.rowIndex;
      this.hidePriceError(rowIndex);
    });
  }

  // Unit error methods
  showUnitError(rowIndex) {
    const errorElement = this.getUnitErrorForRow(rowIndex);
    if (errorElement) {
      errorElement.classList.remove("hidden");

      // Add error styling to the unit field
      const unitConsistent = this.getUnitConsistentForRow(rowIndex);
      if (unitConsistent) {
        unitConsistent.classList.add("ring-2", "ring-error/50", "bg-error/5");
      }
    }
  }

  hideUnitError(rowIndex) {
    const errorElement = this.getUnitErrorForRow(rowIndex);
    if (errorElement) {
      errorElement.classList.add("hidden");

      // Remove error styling from the unit field
      const unitConsistent = this.getUnitConsistentForRow(rowIndex);
      if (unitConsistent) {
        unitConsistent.classList.remove(
          "ring-2",
          "ring-error/50",
          "bg-error/5"
        );
      }
    }
  }

  getUnitErrorForRow(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    return row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="rowUnitError"]'
    );
  }

  // Price error methods
  showPriceError(rowIndex) {
    const errorElement = this.getPriceErrorForRow(rowIndex);
    if (errorElement) {
      errorElement.classList.remove("hidden");

      // Add error styling to the price field
      const priceConsistent = this.getPriceConsistentForRow(rowIndex);
      if (priceConsistent) {
        priceConsistent.classList.add("ring-2", "ring-error/50", "bg-error/5");
      }
    }
  }

  hidePriceError(rowIndex) {
    const errorElement = this.getPriceErrorForRow(rowIndex);
    if (errorElement) {
      errorElement.classList.add("hidden");

      // Remove error styling from the price field
      const priceConsistent = this.getPriceConsistentForRow(rowIndex);
      if (priceConsistent) {
        priceConsistent.classList.remove(
          "ring-2",
          "ring-error/50",
          "bg-error/5"
        );
      }
    }
  }

  getPriceErrorForRow(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    return row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="rowPriceError"]'
    );
  }

  getQuantityErrorForRow(rowIndex) {
    const row = this.getRowByIndex(rowIndex);
    return row?.querySelector(
      '[data-ingredients-table-spreadsheet-target="rowQuantityError"]'
    );
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  disconnect() {
    // Clean up timeouts
    this.searchTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.searchTimeouts.clear();
    // Clean up dropdown scroll/resize listeners
    this.untrackOpenDropdown();
  }
}
